import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import { defineComponent } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

const props = {
  /**
   * The HTML element to render as
   * @default "div"
   */
  as: p.string<keyof HTMLElementTagNameMap>("div"),
} satisfies ComponentPropsOptions;

export type SkeletonProps = ExtractPublicPropTypes<typeof props>;

/**
 * Skeleton component - Loading placeholder with pulse animation
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Skeleton class="h-4 w-[250px]" />
 *
 * // Avatar pattern
 * <div class="flex items-center gap-4">
 *   <Skeleton class="h-12 w-12 rounded-full" />
 *   <div class="space-y-2">
 *     <Skeleton class="h-4 w-[250px]" />
 *     <Skeleton class="h-4 w-[200px]" />
 *   </div>
 * </div>
 *
 * // Card pattern
 * <div class="flex flex-col gap-3">
 *   <Skeleton class="h-[125px] w-[250px] rounded-xl" />
 *   <div class="space-y-2">
 *     <Skeleton class="h-4 w-[250px]" />
 *     <Skeleton class="h-4 w-[200px]" />
 *   </div>
 * </div>
 * ```
 */
const Skeleton = defineComponent({
  name: "Skeleton",
  props,
  setup(props, { attrs }) {
    const Component = props.as;

    return () => (
      <Component
        {...attrs}
        class={cn(
          // Base skeleton styles from shadcn-ui
          "animate-pulse rounded-md bg-muted",
          // User-provided classes via attrs
          attrs.class ?? ""
        )}
      />
    );
  },
});

export default Skeleton;
